/** Pure data operations shared by the grid and its regression tests. */
export const rowId = (row, getRowId) => typeof getRowId === 'function' ? getRowId(row) : row.id;
export function cellParams(row, column, getRowId, api) {
    const params = { id: rowId(row, getRowId), row, field: column.field, colDef: column, value: row[column.field], api };
    if (column.valueGetter)
        params.value = column.valueGetter(params);
    params.formattedValue = column.valueFormatter ? column.valueFormatter(params) : params.value;
    return params;
}
const normalize = v => String(v ?? '').toLocaleLowerCase();
export function matchesFilter(row, column, item, getRowId, api) {
    if (!column)
        return true;
    const params = cellParams(row, column, getRowId, api);
    const value = params.value, query = item.value, operator = item.operatorValue || item.operator || 'contains';
    const custom = column.filterOperators?.find(op => op.value === operator);
    if (custom) {
        const predicate = custom.getApplyFilterFn?.(item, column);
        return !predicate || predicate(params);
    }
    if (!['isEmpty', 'isNotEmpty'].includes(operator) && (query == null || query === ''))
        return true;
    switch (operator) {
        case 'contains': return normalize(value).includes(normalize(query));
        case 'doesNotContain': return !normalize(value).includes(normalize(query));
        case 'equals':
        case 'is': return column.type === 'date' || column.type === 'dateTime' ? new Date(value).getTime() === new Date(query).getTime() : normalize(value) === normalize(query);
        case 'not':
        case 'doesNotEqual': return normalize(value) !== normalize(query);
        case 'startsWith': return normalize(value).startsWith(normalize(query));
        case 'endsWith': return normalize(value).endsWith(normalize(query));
        case 'isEmpty': return value == null || value === '';
        case 'isNotEmpty': return value != null && value !== '';
        case 'isAnyOf': return Array.isArray(query) && query.some(q => normalize(q) === normalize(value));
        case '>': return Number(value) > Number(query);
        case '>=': return Number(value) >= Number(query);
        case '<': return Number(value) < Number(query);
        case '<=': return Number(value) <= Number(query);
        case '=': return Number(value) === Number(query);
        case '!=': return Number(value) !== Number(query);
        case 'after': return new Date(value) > new Date(query);
        case 'onOrAfter': return new Date(value) >= new Date(query);
        case 'before': return new Date(value) < new Date(query);
        case 'onOrBefore': return new Date(value) <= new Date(query);
        default: return true;
    }
}
export function filterRows(rows, columns, model = {}, getRowId, api) {
    const items = model.items || [], quick = model.quickFilterValues || [];
    const logic = (model.logicOperator || model.linkOperator || 'and').toLowerCase();
    return rows.filter(row => {
        const checks = items.map(item => matchesFilter(row, columns.find(c => c.field === (item.columnField || item.field)), item, getRowId, api));
        if (checks.length && !(logic === 'or' ? checks.some(Boolean) : checks.every(Boolean)))
            return false;
        const quickChecks = quick.map(query => columns.some(col => normalize(cellParams(row, col, getRowId, api).formattedValue).includes(normalize(query))));
        return !quickChecks.length || ((model.quickFilterLogicOperator || 'and') === 'or' ? quickChecks.some(Boolean) : quickChecks.every(Boolean));
    });
}
export function sortRows(rows, columns, sortModel = [], getRowId, api) {
    if (!sortModel.length)
        return rows.slice();
    return rows.map((row, index) => ({ row, index })).sort((a, b) => {
        for (const item of sortModel) {
            if (!item.sort)
                continue;
            const col = columns.find(c => c.field === item.field);
            if (!col)
                continue;
            const p = cellParams(a.row, col, getRowId, api), q = cellParams(b.row, col, getRowId, api);
            let result;
            if (col.sortComparator)
                result = col.sortComparator(p.value, q.value, p, q);
            else if (p.value == null || q.value == null)
                result = p.value == null ? q.value == null ? 0 : -1 : 1;
            else if (col.type === 'number' || typeof p.value === 'number' && typeof q.value === 'number')
                result = Number(p.value) - Number(q.value);
            else if (col.type === 'date' || col.type === 'dateTime')
                result = new Date(p.value).getTime() - new Date(q.value).getTime();
            else
                result = String(p.value).localeCompare(String(q.value));
            if (result)
                return item.sort === 'desc' ? -result : result;
        }
        return a.index - b.index;
    }).map(item => item.row);
}
export function gridCsv(rows, columns, { delimiter = ',', includeHeaders = true, escapeFormulas = true } = {}, getRowId, api) {
    const quote = value => { let text = String(value ?? ''); if (escapeFormulas && /^[=+\-@\t\r]/.test(text))
        text = "'" + text; return '"' + text.replace(/"/g, '""') + '"'; };
    const lines = includeHeaders ? [columns.map(c => quote(c.headerName || c.field)).join(delimiter)] : [];
    rows.forEach(row => lines.push(columns.map(c => quote(cellParams(row, c, getRowId, api).formattedValue)).join(delimiter)));
    return lines.join('\r\n');
}
